import { getCaptionString, Translation } from '@modeso/aumico-lib-core-fe';
import { EMPTY_STRING } from '@modeso/aumico-lib-settings-fe';
import {
    IEquity,
    IFinancialLineItemComponent,
    IFreeTable,
    IMovementScheduleComponent,
    IMovementScheduleFli,
    INotesInputComponent,
    INotesSelectComponent,
    ISuperTableComponent,
    ITranslationEntry,
    SuperTableRowType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ComponentType, IRichText, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
// Not re-exported from the package barrel; the print mappers import it by the same deep path.
import { IInputDataType } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/input-type.enum';
import { INotesTypeEntityDTO } from '@modeso/types__aumico-lib-notes-be';
import { ItemType } from '@modeso/types__aumico-lib-templates-be';
import { GapReportingVisitor } from './GapReportingVisitor';
import { ITranslatorVisitor } from './ITranslatorVisitor.interface';
import { UntranslatedTexts } from './UntranslatedTexts';
import { Notes } from './classes/Notes';

export class NotesTranslatorVisitor extends GapReportingVisitor implements ITranslatorVisitor {
    constructor(
        private readonly targetLanguage: string,
        private readonly translatableItems: Map<string, Translation>,
        private reportLanguage: string,
        untranslated?: UntranslatedTexts,
        private readonly notesTypes?: INotesTypeEntityDTO[],
        translations?: Record<string, ITranslationEntry>,
    ) {
        super(untranslated, 'notes', translations);
    }

    visit(notes: Notes): void {
        notes.notes.accordions.forEach((accordion) => {
            // Renaming writes only the report locale, so a foreign language goes stale rather than empty.
            // Matches the template → the template's target-language title is the fallback; renamed (or no
            // template) → no fallback, so the block fills it or it is reported as a gap.
            const masterTitle = this.masterSectionTitle(accordion.name);
            const sourceTitle = accordion.title?.[this.reportLanguage];
            const matchesTemplate = masterTitle != null && sourceTitle === masterTitle;
            if (accordion.title != null) {
                accordion.title[this.targetLanguage] = this.resolve(
                    sourceTitle,
                    matchesTemplate ? this.masterSectionTitle(accordion.name, this.targetLanguage) ?? EMPTY_STRING : EMPTY_STRING,
                );
            }

            accordion.data.forEach((component) => {
                // Before the branch chain, so it covers every component type. The template carries neither field.
                const componentTitle = component.componentTitle;
                if (componentTitle != null) {
                    componentTitle[this.targetLanguage] = this.resolve(
                        componentTitle[this.reportLanguage],
                        componentTitle[this.targetLanguage],
                    );
                }

                const optionsTitle = (component.data as { options?: { title?: Record<string, string> } })?.options
                    ?.title;
                if (optionsTitle != null) {
                    optionsTitle[this.targetLanguage] = this.resolve(
                        optionsTitle[this.reportLanguage],
                        optionsTitle[this.targetLanguage],
                    );
                }

                // Never supplied by the template, so every filled entry is a gap. hasFootnotes is deliberately ignored.
                const footnotes = (component.data as { footnotes?: string[] })?.footnotes;
                footnotes?.forEach((footnote, index) => (footnotes[index] = this.resolve(footnote)));

                if (component.componentName === ComponentType.FINANCIAL_LINE_ITEM) {
                    const fliComponent = component.data as IFinancialLineItemComponent;

                    if (fliComponent.options.text != null) {
                        fliComponent.options.text[this.targetLanguage] = this.resolve(
                            fliComponent.options.text[this.reportLanguage],
                            fliComponent.options.text[this.targetLanguage],
                        );
                    }

                    fliComponent.flis.forEach((fli) => {
                        const name = `${fli.id}_${ItemType.FLI}`;
                        const translation = this.translatableItems.get(name);
                        // Read before the assignment overwrites it; may still be a locale object.
                        const sourceCaption = getCaptionString(fli.caption, this.reportLanguage);

                        if (translation != null) {
                            const translated = translation[this.targetLanguage] ?? EMPTY_STRING;
                            // Master data wins; an empty target is a gap the block may fill.
                            fli.caption = this.resolve(sourceCaption, translated);

                            /** Fli is created by user */
                        } else {
                            fli.caption = this.resolve(sourceCaption);
                        }

                        if (fli.totalRow != null) {
                            fli.totalRow.accountName = this.resolve(fli.totalRow.accountName);
                        }
                        fli.accounts.forEach(
                            (account) => (account.accountName = this.resolve(account.accountName)),
                        );
                    });
                } else if (component.componentName === ComponentType.MOVEMENT_SCHEDULE) {
                    const movementScheduleComponent = component.data as IMovementScheduleComponent;
                    movementScheduleComponent.flis.forEach((fli) => {
                        const name = `${fli.id}_${ItemType.FLI}`;
                        const translation = this.translatableItems.get(name);

                        if (translation != null) {
                            const translated = translation[this.targetLanguage] ?? EMPTY_STRING;
                            fli.caption[this.targetLanguage] = this.resolve(fli.caption?.[this.reportLanguage], translated);

                            /** Fli is created by user */
                        } else if (typeof fli.caption === 'object') {
                            fli.caption[this.targetLanguage] = this.resolve(fli.caption[this.reportLanguage]);
                        }

                        this.reportMovementScheduleFliFields(fli);
                    });

                    if (movementScheduleComponent.previousMovementScheduleComponent != null) {
                        movementScheduleComponent.previousMovementScheduleComponent.flis.forEach((fli) => {
                            const name = `${fli.id}_${ItemType.FLI}`;
                            const translation = this.translatableItems.get(name);
                            if (translation != null) {
                                const translated = translation[this.targetLanguage] ?? EMPTY_STRING;
                                fli.caption[this.targetLanguage] = this.resolve(fli.caption?.[this.reportLanguage], translated);

                                /** Fli is created by user */
                            } else if (typeof fli.caption === 'object') {
                                fli.caption[this.targetLanguage] = this.resolve(fli.caption[this.reportLanguage]);
                            }

                            this.reportMovementScheduleFliFields(fli);
                        });
                    }
                } else if (component.componentName === ComponentType.SELECT) {
                    const selectComponent = component.data as INotesSelectComponent;

                    if (selectComponent.options.text != null) {
                        selectComponent.options.text[this.targetLanguage] = this.resolve(
                            selectComponent.options.text[this.reportLanguage],
                            selectComponent.options.text[this.targetLanguage],
                        );
                    }

                    const currentYearValueOption = selectComponent.options.selectionOption.find(
                        (option) => option[this.reportLanguage] === selectComponent.currentYearValue,
                    );
                    // Option missing prints an empty cell, option without target language prints the report language.
                    selectComponent.currentYearValue = this.resolve(
                        selectComponent.currentYearValue,
                        currentYearValueOption != null
                            ? currentYearValueOption[this.targetLanguage] || currentYearValueOption[this.reportLanguage]
                            : EMPTY_STRING,
                    );

                    const previousYearValueOption = selectComponent.options.selectionOption.find(
                        (option) => option[this.reportLanguage] === selectComponent.previousYearValue,
                    );
                    selectComponent.previousYearValue = this.resolve(
                        selectComponent.previousYearValue,
                        previousYearValueOption != null
                            ? previousYearValueOption[this.targetLanguage] || previousYearValueOption[this.reportLanguage]
                            : EMPTY_STRING,
                    );
                } else if (component.componentName === ComponentType.RICH_TEXT) {
                    const richTextComponents = component.data as IRichText[];
                    const translatedRichText = richTextComponents.find(
                        (richText) => richText.language === this.targetLanguage,
                    );
                    const sourceRichText = richTextComponents.find(
                        (richText) => richText.language === this.reportLanguage,
                    );
                    const masterText = this.masterRichText(accordion.name, component.componentId);
                    // No gap only when the target slot is filled and the report text still matches the template.
                    const isRichTextGap =
                        !translatedRichText ||
                        !translatedRichText.text ||
                        masterText == null ||
                        sourceRichText?.text !== masterText;

                    if (translatedRichText != null) {
                        // Gap → no fallback, so resolve fills from the block or keeps the report text
                        // (reproduces the old copy-into-empty-target); no gap → the existing target
                        // translation. resolve checks the user edit first either way.
                        translatedRichText.text = isRichTextGap
                            ? this.resolve(sourceRichText?.text)
                            : this.resolve(sourceRichText?.text, translatedRichText.text);
                        translatedRichText.title = isRichTextGap
                            ? this.resolve(sourceRichText?.title)
                            : this.resolve(sourceRichText?.title, translatedRichText.title);
                    }
                } else if (component.componentName === ComponentType.FREE_TABLE_WITH_AMOUNTS) {
                    // The template stores options only, never rows, so every filled text field is a gap.
                    const freeTable = component.data as IFreeTable;

                    freeTable.rows.forEach((row) => (row.text = this.resolve(row.text)));
                    if (freeTable.totalRow != null) {
                        freeTable.totalRow.text = this.resolve(freeTable.totalRow.text);
                    }
                } else if (component.componentName === ComponentType.SUPER_TABLE) {
                    // All user content, balance-sheet rows included: every row type prints its text from rows[].title.
                    const superTable = component.data as ISuperTableComponent;
                    const { options } = superTable;

                    options.segments.forEach((segment) => {
                        if (segment.title != null) {
                            segment.title[this.targetLanguage] = this.resolve(
                                segment.title[this.reportLanguage],
                                segment.title[this.targetLanguage],
                            );
                        }
                    });
                    options.xAxisConfig.forEach((column) => {
                        if (column.title != null) {
                            column.title[this.targetLanguage] = this.resolve(
                                column.title[this.reportLanguage],
                                column.title[this.targetLanguage],
                            );
                        }
                        // Free text printed on a second line under the column title, one string for every language.
                        column.currency = this.resolve(column.currency);
                    });

                    const textColumnIds = options.xAxisConfig
                        .filter((column) => column.type === SuperTableColumnType.TEXT)
                        .map((column) => column.columnId);
                    const allColumnIds = options.xAxisConfig.map((column) => column.columnId);

                    superTable.rows.forEach((row) => {
                        row.title = this.resolve(row.title);

                        // On TITLE and TEXT_ROW every cell is text, year columns included; otherwise only TEXT columns.
                        const isTextRow =
                            row.type === SuperTableRowType.TITLE || row.type === SuperTableRowType.TEXT_ROW;
                        if (isTextRow) {
                            // In a text row these cells hold text, though the type declares them numeric.
                            const textCells = row as unknown as Record<string, unknown>;
                            (['currentValue', 'previousValue'] as const).forEach((cellKey) => {
                                const cell = textCells[cellKey];
                                if (typeof cell === 'string') {
                                    textCells[cellKey] = this.resolve(cell);
                                }
                            });
                        }
                        (isTextRow ? allColumnIds : textColumnIds).forEach((columnId) => {
                            const cell = row[columnId];
                            if (typeof cell === 'string') {
                                row[columnId] = this.resolve(cell);
                            }
                        });
                    });
                } else if (component.componentName === ComponentType.INPUT) {
                    const input = component.data as INotesInputComponent;

                    if (input.options.text != null) {
                        input.options.text[this.targetLanguage] = this.resolve(
                            input.options.text[this.reportLanguage],
                            input.options.text[this.targetLanguage],
                        );
                    }

                    // Only dataType TEXT holds text; NUMBER and CURRENCY are numbers.
                    if (input.options.dataType === IInputDataType.TEXT) {
                        if (typeof input.currentYearValue === 'string') {
                            input.currentYearValue = this.resolve(input.currentYearValue);
                        }
                        if (typeof input.previousYearValue === 'string') {
                            input.previousYearValue = this.resolve(input.previousYearValue);
                        }
                    }
                } else if (component.componentName === ComponentType.EQUITY) {
                    // Legacy component. Needs its own branch: its titles are textTitle and …ValueTitle1/2.
                    const equity = component.data as IEquity;
                    const { options } = equity;

                    [
                        options.textTitle,
                        options.currentValueTitle1,
                        options.currentValueTitle2,
                        options.previousValueTitle1,
                        options.previousValueTitle2,
                    ].forEach((field) => {
                        if (field != null) {
                            field[this.targetLanguage] = this.resolve(
                                field[this.reportLanguage],
                                field[this.targetLanguage],
                            );
                        }
                    });

                    equity.rows.forEach((row) => (row.text = this.resolve(row.text)));
                    if (equity.totalRow != null) {
                        equity.totalRow.text = this.resolve(equity.totalRow.text);
                    }
                }
            });
        });
    }

    /** The template title of a section in the given language, or null when there is no template entry. */
    private masterSectionTitle(accordionName: string | undefined, language: string = this.reportLanguage): string | null {
        const notesType = this.notesTypes?.find((type) => type.name === accordionName);

        return notesType?.title?.[language] ?? null;
    }

    /** The template text of one rich-text component in the report language, matched on componentId. */
    private masterRichText(accordionName: string | undefined, componentId: string): string | null {
        const notesType = this.notesTypes?.find((type) => type.name === accordionName);
        const componentOptions = notesType?.componentsOptions?.find((options) => options.componentId === componentId)
            ?.componentOptions as IRichText[] | undefined;

        return componentOptions?.find((richText) => richText.language === this.reportLanguage)?.text ?? null;
    }

    /** The movement-schedule fields the visitor does not otherwise touch; same for the previous-year schedule. */
    private reportMovementScheduleFliFields(fli: IMovementScheduleFli): void {
        (['column1', 'column2', 'column3', 'column4', 'title'] as const).forEach((column) => {
            const header = fli.headers[column];
            if (header != null) {
                header[this.targetLanguage] = this.resolve(header[this.reportLanguage], header[this.targetLanguage]);
            }
        });

        if (fli.totalRow != null) {
            fli.totalRow.text = this.resolve(fli.totalRow.text);
        }
        fli.accounts.forEach((account) => (account.text = this.resolve(account.text)));
    }
}
